import {
  Button,
  Col,
  Empty,
  Form,
  Header,
  Icon,
  Input,
  Loading,
  Row,
  Space,
  Widget,
  Write,
  Zone,
} from '@gimlite/ui-v1';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import OspFull from '../../assets/logos/osp_full';
import {
  Mutation,
  MutationVerifyAndUpdateMotoristArgs,
  Query,
  QueryVerifyMotoristArgs,
} from '../../client/graphql';
import { verifyAndUpdateMotoristGQL } from '../../common/gql/verifyAndUpdateMotorist.gql';
import { verifyMotoristGQL } from '../../common/gql/verifyMotorist.gql';
import { config } from '../../config';
import './motorist-confirm.scss';
import {
  digitRule,
  lowercaseLetterRule,
  minLengthPasswordRule,
  specialCharRule,
  uppercaseLetterRule,
  useLazyQuery,
  useMutation,
  useMyUrl,
  useTranslation,
} from '@gimlite/common';

const debug = false;

export const MotoristConfirm = observer(() => {
  const { t } = useTranslation();
  const { getParamsUrl } = useMyUrl<{ token: string }>();
  const [error, setError] = useState<'LINK' | 'TOKEN' | null>();
  const [passwordValidFormat, setPasswordValidForm] = useState(false);
  const [passwordValidSame, setPasswordValidSame] = useState(false);
  const [passwordValues, setPasswordValues] = useState<Object>({});

  const [verifyMotoristCall, verifyMotoristState] = useLazyQuery<
    {
      verifyMotorist: Query['verifyMotorist'];
    },
    QueryVerifyMotoristArgs
  >(verifyMotoristGQL);

  const [verifyAndUpdateMotoristCall, verifyAndUpdateMotoristState] =
    useMutation<
      {
        verifyAndUpdateMotorist: Mutation['verifyAndUpdateMotorist'];
      },
      MutationVerifyAndUpdateMotoristArgs
    >(verifyAndUpdateMotoristGQL, {
      notification: {
        success: t('thePasswordHasBeenRegistered'),
        error: t('thePasswordCouldNotBeRegistered'),
      },
    });

  useEffect(() => {
    if (getParamsUrl.token) {
      verifyMotoristCall({ variables: { token: getParamsUrl.token } });
    } else {
      setError(() => 'LINK');
    }
  }, [getParamsUrl]);

  useEffect(() => {
    if (verifyMotoristState.error) {
      setError(() => 'TOKEN');
    }
  }, [verifyMotoristState.error]);

  useEffect(() => {
    if (verifyAndUpdateMotoristState.data) {
      if (!debug) {
        window.location.replace(
          config.FRONT_OSP_MOTORISTS_HOST + '?origin=password_registered',
        );
      }
    }
  }, [verifyAndUpdateMotoristState.data]);

  return (
    <div className="motoristConfirm">
      <Header config={{ logo: OspFull(), withAccount: false }}></Header>
      {verifyMotoristState.loading ? (
        <div className="motoristConfirm__load">
          <Loading></Loading>
          <Write
            className="motoristConfirm__load__text"
            data={{ item: t('waitingForServerResponse') }}
            config={{ mode: 'value-medium' }}
          ></Write>
        </div>
      ) : error ? (
        <div className="motoristConfirm__error">
          <Icon
            config={{
              type: 'faCircleExclamationSolid',
              color: 'error',
              size: 'giga',
            }}
          ></Icon>
          <Write
            className="motoristConfirm__error__text"
            data={{
              item:
                error !== 'LINK'
                  ? t('linkIsNoLongerValid')
                  : t('linkIsNotValid'),
            }}
            config={{ mode: 'value-large', color: 'error' }}
          ></Write>
          {error !== 'LINK' && (
            <Button
              handleEvent={{
                click: () =>
                  window.location.replace(
                    config.FRONT_OSP_MOTORISTS_HOST +
                      '?origin=password_registered',
                  ),
              }}
              className="motoristConfirm__error__button"
              config={{ text: t('goToPortalMotorist') }}
            ></Button>
          )}
        </div>
      ) : (
        <Zone
          className="motoristConfirm__contain"
          config={{
            zones: [['auth']],
            rows: ['1fr'],
            columns: ['1fr'],
          }}
        >
          <Zone.Area
            config={{
              area: 'auth',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: '100%',
                height: '100%',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  width: '700px',
                }}
              >
                <Widget config={{ title: t('newAccount') }}>
                  <Form
                    config={{
                      validateOnChange: true,
                    }}
                    data={{
                      value: passwordValues,
                    }}
                    handleEvent={{
                      submit: async (value) => {
                        await verifyAndUpdateMotoristCall({
                          variables: {
                            input: {
                              token: getParamsUrl.token!,
                              dataToValidate: {
                                username:
                                  verifyMotoristState.data?.verifyMotorist
                                    .contract.username!,
                                password: value.newPassword,
                                lang: verifyMotoristState.data?.verifyMotorist
                                  .contract.lang!,
                              },
                            },
                          },
                        });
                      },
                      validate: (isValid) => {
                        setPasswordValidForm(() => isValid);
                      },
                      change: (values) => {
                        setPasswordValues(() => values);
                        setPasswordValidSame(
                          () =>
                            values.newPassword === values.confirmNewPassword,
                        );
                      },
                    }}
                  >
                    <Form.Item
                      config={{
                        name: 'newPassword',
                        label: t('newPassword'),
                        way: 'vertical',
                        rules: [
                          { required: true },
                          {
                            validator: minLengthPasswordRule,
                          },
                          {
                            validator: uppercaseLetterRule,
                          },
                          {
                            validator: lowercaseLetterRule,
                          },
                          {
                            validator: digitRule,
                          },
                          {
                            validator: specialCharRule,
                          },
                        ],
                      }}
                    >
                      <Input
                        config={{
                          type: { name: 'password' },
                          height: 'xlarge',
                          width: 'full',
                        }}
                      ></Input>
                    </Form.Item>
                    <Space config={{ way: 'vertical', count: 2 }}></Space>
                    <Form.Item
                      config={{
                        name: 'confirmNewPassword',
                        label: t('confirmNewPassword'),
                        way: 'vertical',
                        rules: [
                          { required: true },
                          {
                            validator: minLengthPasswordRule,
                          },
                          {
                            validator: uppercaseLetterRule,
                          },
                          {
                            validator: lowercaseLetterRule,
                          },
                          {
                            validator: digitRule,
                          },
                          {
                            validator: specialCharRule,
                          },
                        ],
                      }}
                    >
                      <Input
                        config={{
                          type: { name: 'password' },
                          height: 'xlarge',
                          width: 'full',
                        }}
                      ></Input>
                    </Form.Item>
                    <Space config={{ way: 'vertical', count: 2 }}></Space>
                    <Button
                      config={{
                        text: t('registerPassword'),
                        type: { value: 'submit' },
                        disabled: !(passwordValidFormat && passwordValidSame),
                      }}
                    ></Button>
                    <Space config={{ way: 'vertical', count: 2 }} />

                    {passwordValidFormat && !passwordValidSame && (
                      <Col
                        config={{
                          width: 'full',
                          vertical: 'center',
                          horizontal: 'center',
                        }}
                      >
                        <Write
                          data={{ item: t('passwordsAreDifferent') }}
                          config={{ color: 'error', mode: 'key-small-light' }}
                        />
                      </Col>
                    )}
                  </Form>
                </Widget>
              </div>
            </div>
          </Zone.Area>
        </Zone>
      )}
    </div>
  );
});
